// src/components/UserManagement.jsx
//
// Pattern: ตาม MenuManagement / USAShipmentDatagrid
//   - Container / Grid จาก @material-ui/core
//   - Button1 จาก @material-ui/core/Button
//   - useStyles + className="head" / "searchButton"
//   - Toast fixed top-right
//   - Modal inline style
//
import React, { useEffect, useState, useCallback } from "react";
import Button1    from "@material-ui/core/Button";
import Grid       from "@material-ui/core/Grid";
import Container  from "@material-ui/core/Container";

import { useStyles }     from "../../Styles.js";
import { useUsers }      from "../../hooks/useUsers";
import { UserList }      from "./UserList";
import { UserDetail }    from "./UserDetail";
import { UserForm }      from "./UserForm";
import { ConfirmDialog } from "../Menu/ConfirmDialog";  // reuse จาก menu management
import { Toast }         from "../Menu/Toast";           // reuse จาก menu management

// ─────────────────────────────────────────────────────────────────────────────
export function UserManagement() {
  const classes = useStyles();
  const {
    users, loading, error,
    clearError, fetchAll,
    create, update, remove,
  } = useUsers();

  const [selectedId,  setSelectedId]  = useState(null);
  const [formOpen,    setFormOpen]    = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast,       setToast]       = useState(null);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const selectedUser = users.find(u => u.CRM_UID === selectedId) ?? null;

  // ── Open add form ─────────────────────────────────────────────────────────
  const handleOpenAdd = useCallback(() => {
    setEditingUser(null);
    setFormOpen(true);
  }, []);

  // ── Open edit form ────────────────────────────────────────────────────────
  const handleOpenEdit = useCallback(() => {
    setEditingUser(selectedUser);
    setFormOpen(true);
  }, [selectedUser]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (payload) => {
    try {
      if (editingUser?.CRM_UID) {
        const updated = await update(editingUser.CRM_UID, payload);
        setSelectedId(updated.CRM_UID);
        showToast(`✅ แก้ไข "${updated.USER_FIRSTNAME} ${updated.USER_LASTNAME}" สำเร็จ`);
      } else {
        const created = await create(payload);
        setSelectedId(created.CRM_UID);
        showToast(`✅ เพิ่ม "${created.USER_FIRSTNAME} ${created.USER_LASTNAME}" สำเร็จ`);
      }
      setFormOpen(false);
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    try {
      await remove(selectedId);
      setSelectedId(null);
      setConfirmOpen(false);
      showToast("🗑️ ลบ user สำเร็จ");
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  const deleteMsg = `ยืนยันลบ "${selectedUser?.USER_FIRSTNAME} ${selectedUser?.USER_LASTNAME}"?\nอีเมล: ${selectedUser?.USER_EMAIL}\nไม่สามารถกู้คืนได้`;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl">
      <Toast toast={toast} />

      {/* Modals */}
      <UserForm
        open={formOpen}
        user={editingUser}
        onSave={handleSave}
        onClose={() => setFormOpen(false)}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="🗑️ ลบ User"
        message={deleteMsg}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Spacer */}
      <Grid container spacing={2}>
        <Grid item xs={12}>&nbsp;</Grid>
      </Grid>

      {/* Page title */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">User Management</label>
        </Grid>
      </Grid>

      {/* Action buttons */}
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Button1
            className="searchButton"
            variant="contained"
            color="primary"
            onClick={() => fetchAll()}
            disabled={loading}
          >
            {loading ? "⏳ Loading..." : "Refresh"}
          </Button1>

          <Button1
            className="searchButton"
            variant="outlined"
            color="primary"
            onClick={handleOpenAdd}
          >
            ➕ Add User
          </Button1>

          {/* Error banner */}
          {error && (
            <span
              onClick={clearError}
              style={{
                background: "#fef2f2", color: "#dc2626",
                border: "1px solid #fca5a5", borderRadius: 6,
                padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              ❌ {error} (คลิกเพื่อปิด)
            </span>
          )}
        </Grid>
      </Grid>

      {/* Main layout */}
      <Grid container className={classes.root} style={{ marginTop: 12 }}>
        <div style={{
          display: "flex", width: "100%",
          border: "1px solid #e2e8f0", borderRadius: 8,
          overflow: "hidden", minHeight: 600, background: "#fff",
        }}>
          {/* Left: User list */}
          <UserList
            users={users}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={handleOpenAdd}
          />

          {/* Right: Detail */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <UserDetail
              user={selectedUser}
              onEdit={handleOpenEdit}
              onDelete={() => setConfirmOpen(true)}
            />
          </div>
        </div>
      </Grid>
    </Container>
  );
}
